import * as React from 'react';
import {Button, Col, Row} from 'react-bootstrap';
import {destroy, formValueSelector, reduxForm} from 'redux-form';
import {bindActionCreators} from 'redux';
import {push} from 'react-router-redux';
import {Link, RouteComponentProps} from 'react-router-dom';
import {flow, forEach, last, map, set, get, each} from 'lodash';
import {connect} from 'react-redux';
import {IAuthState} from '../../../../services/auth/models';
import {IPolicy, IPolicyApplication} from '../../../../data/api/models';
import {FormattedNumber} from 'react-intl';
import {actions as brandingActions} from '../../../../services/branding/reducer';
import {IBrandingActions, IBrandingState} from '../../../../services/branding/models';
import {IQuoteActions, IQuoteState} from '../../../../services/quote/models';
import {actions as quoteActions} from '../../../../services/quote/reducer';
import {actions as authActions, IAuthActions} from '../../../../services/auth/reducer';
import FaSpinner from 'react-icons/fa/spinner';
import FaFileTextO from 'react-icons/fa/file-text-o';
import Helmet from 'react-helmet';
import {IStore} from '../../../../redux/IStore';
import steps from '../../../Quote/steps';
import NonAutoRenewalButton from '../../components/NonAutoRenewalButton';
import PaymentModal from '../../../Quote/components/PaymentModal';

interface IPolicyDetailProps extends RouteComponentProps<{}> {
  quote: IQuoteState;
  auth: IAuthState;
  branding: IBrandingState;
  dispatch: any;
  actions: {
    auth: IAuthActions;
    push: (route: string) => void;
    calculate: any;
    branding: IBrandingActions,
    quote: IQuoteActions;
    destroy(formName: string);
  };
}

interface IPolicyDetailStates {
  showModal: boolean;
  isRetrievingQuote: boolean;
  autoRenewalState: string;
  transactionId: string | null;
  values: any;
}

class PolicyDetail extends React.Component<IPolicyDetailProps, IPolicyDetailStates> {
  public state = {
    showModal: false,
    isRetrievingQuote: false,
    autoRenewalState: 'On',
    transactionId: null,
    values: null,
  };

  private handleGetNewQuote = () => {
    const { actions } = this.props;

    actions.destroy('quote');
    actions.quote.reset();
    actions.quote.loadDestinations();
    actions.push('/');
  }

  private renderPaymentModal = () => {
      return (
          <PaymentModal
              transactionId={this.state.transactionId}
              actions={this.props.actions}
              showModal={this.state.showModal}
          />
      );
  }

  private scrollToTop = () => {
    if (process.env.BROWSER) {
      window.scrollTo(0, 0);
    }
  }

  private handleEditQuote = () => {
    const {actions, auth, quote, branding} = this.props;
    this.setState({
      isRetrievingQuote: true,
    });

    let selectedQuote: IPolicyApplication = null;

    forEach(auth.customer.policyApplications, (quoteSelected) => {
      if (location.pathname.match(quoteSelected.id)) {
        selectedQuote = quoteSelected;
      }
    });

    // can remove below?
    // Re-init form
    actions.destroy('quote');
    actions.quote.reset();

    const product = actions.branding.getProduct();
    const lastStep = last(selectedQuote.steps);
    const quoteSteps = steps(branding.channel);

    quoteSteps.forEach((step) => {
      if (step.key !== lastStep) {
        return;
      }
    });

    actions.quote.initFromProduct(product, quote, branding);
    actions.quote.loadDestinations();

    if (selectedQuote.schemeId && lastStep === 'schemeChoice') {
      set(selectedQuote, 'schemeId',  '');
    }

    // Set some data according to the channel
    if (selectedQuote.channel) {
      const channelToCalculate = selectedQuote.channel;
      actions.branding.loadChannel(channelToCalculate.key).then(
        actions.quote.retrieveQuote(selectedQuote),
      );
    }
    selectedQuote.purchaseChannel = branding.channel.key;
    if (!selectedQuote.steps || selectedQuote.steps.length === 0) {
        selectedQuote.steps = ['complete'];
    }
    actions.quote.setApplication(selectedQuote);

    return actions.quote.submitForm(selectedQuote, true, false, false)
        .then(() => {
            return actions.quote.purchase(selectedQuote)
                .then((data) => {
                    actions.quote.complete(data.policies[0]);
                    actions.quote.addStepToWizard('complete', false);

                    actions.push('complete');

                    this.scrollToTop();
                })
                .catch((error) => {
                    let rethrow = true;
                    let message = null;

                    each(error.response.data.errors, (e: any) => {
                        if (e.id !== 'redirect-to-provider') {
                            message = e.detail;
                            return;
                        }

                        this.openModal(selectedQuote, e.data.transactionId);
                        rethrow = false;
                    });

                    if (!rethrow) {
                        return;
                    }

                    if (message) {
                        actions.quote.error(message);
                        this.scrollToTop();
                    }

                    throw error;
                });
        })
        .catch((e) => {
            console.error(e);
        });
  }

  private renderCoverType = (isDisplayingPolicy, policy, selectedQuote) => {
    return (
      <Col sm={6} className="summary-info">
        <Row>
          <Col xs={6}>
            Cover Type
          </Col>
          <Col xs={6} className="info">
            {isDisplayingPolicy ? policy.metadata.scheme_type : selectedQuote.tripType}
          </Col>
        </Row>
      </Col>
    );
  }

  private renderCoverDates = (isDisplayingPolicy, policy, selectedQuote) => {
    const policyDates = (policy) => {
      return (
        <Col xs={6} className="info">{policy.commencesAt} - {policy.expiresAt}</Col>
      );
    };

    const quoteDates = (selectedQuote) => {
      return (
          <Col xs={6} className="info">
            {selectedQuote.startDate} - {selectedQuote.endDate}
          </Col>
      );
    };

    return (
      <Col sm={6} className="summary-info">
        <Row>
          <Col xs={6}>
            Cover Dates
          </Col>
          {isDisplayingPolicy ? policyDates(policy) : quoteDates(selectedQuote)}
        </Row>
      </Col>
    );
  }

  private renderDestinations = (isDisplayingPolicy, policy, selectedQuote) => {
    const productCode = isDisplayingPolicy ? policy.metadata.product_code : selectedQuote.productCode;
    let tripType = isDisplayingPolicy ? policy.metadata.scheme_type : selectedQuote.tripType;

    if (productCode === 'LSB') {
      tripType = 'longstay';
    }

    if (productCode === 'DIA') {
      tripType = 'diabetes';
    }

    const policyDestination = (policy) => {
      return (
        ['annual', 'longstay', 'diabetes'].includes(tripType) ? (
          <Col xs={6} className="info">
            {policy.metadata.region}
          </Col>
        ) : (
          <Col xs={6} className="info">
            {policy.metadata.destinations && policy.metadata.destinations.map((destination, key) => {
              return (
                <p key={key}>{destination}</p>
              );
            })}
          </Col>
        )
      );
    };

    const quoteDestinations = (selectedQuote) => {
      return (
        ['annual', 'longstay', 'diabetes'].includes(tripType) ? (
          <Col xs={6} className="info">
            {selectedQuote.region}
          </Col>
        ) : (
          <Col xs={6} className="info">
            {selectedQuote.destinations && selectedQuote.destinations.map((destination, key) => {
              return (
                <p key={key}>{destination}</p>
              );
            })}
          </Col>
        )
      );
    };

    return (
      <Col sm={6} className="summary-info">
        <Row>
          <Col xs={6}>
            {['annual', 'longstay'].includes(tripType) ? 'Region' : 'Destination'}
          </Col>
          {isDisplayingPolicy ? policyDestination(policy) : quoteDestinations(selectedQuote)}
        </Row>
      </Col>
    );
  }

  private renderOptions = (isDisplayingPolicy, policy, selectedQuote) => {
    if (!policy || !policy.metadata.options || policy.metadata.options.length < 1) {
      return;
    }

    const policyOptions = (policy) => {
      return (
        <Col xs={6} className="info">
          {map(policy.metadata.options, (value, key: string) => {
            if (key === 'cruise_cover') {
              return null;
            }
            if (value === true) {
              return (<p key={key}>{key.replace('_', ' ')}</p>);
            }
          })}
        </Col>
      );
    };

    const quoteOptions = (selectedQuote) => {
      return (
        <Col xs={6} className="info">
          {map(selectedQuote.options, (value, key: string) => {
            if (key === 'cruise_cover') {
              return null;
            }
            if (value === true) {
              return (<p key={key}>{key.replace('_', ' ')}</p>);
            }
          })}
        </Col>
      );
    };

    return (
      <Col sm={6} className="summary-info">
        <Row>
          <Col xs={6}>
            Options
          </Col>
          {isDisplayingPolicy ? policyOptions(policy) : quoteOptions(selectedQuote)}
        </Row>
      </Col>
    );
  }

  private renderStatus = (isDisplayingPolicy, policy, selectedQuote) => {
    return (
      <Col sm={6} className="summary-info">
        <Row>
          <Col xs={6}>
            Status
          </Col>
          <Col xs={6} className="info">
            {isDisplayingPolicy ? policy.status : selectedQuote.status}
          </Col>
        </Row>
      </Col>
    );
  }

  private renderPremium = (isDisplayingPolicy, policy) => {
    const renderPolicyPremium = (policy) => {
      return (
        <Row>
          <Col xs={6}>
            Premium
          </Col>
          <Col xs={6} className="info">
            <FormattedNumber
              value={policy.totalPremiumValue}
              style="currency"
              currency="GBP"
            />
          </Col>
        </Row>
      );
    };

    return (
      <Col sm={6} className="summary-info">
        {isDisplayingPolicy && renderPolicyPremium(policy)}
      </Col>
    );
  }

  private renderDocuments = (isDisplayingPolicy, policy, selectedQuote) => {
    const renderPolicyDocuments = (policy) => {
      return (
        <Row className="customer-documents">
          {policy.documents.map((document, key) => {
            return (
              <Col sm={6} key={key}>
                <div className="documents">
                  <a href={document.filePath} target="_blank">
                    <FaFileTextO/>
                    <span>
                      {document.displayName}
                    </span>
                  </a>
                </div>
              </Col>
            );
          })}
        </Row>
      );
    };

    const renderQuoteDocuments = (selectedQuote) => {
      return (
        <Row className="policy-documents">
          {selectedQuote.documents.map((document, key) => {
            return (
              <Col sm={6} key={key}>
                <div className="documents">
                  <a href={document.filePath} target="_blank">
                    <FaFileTextO/>
                    <span>
                      {document.displayName}
                    </span>
                  </a>
                </div>
              </Col>
            );
          })}
        </Row>
      );
    };

    return (isDisplayingPolicy ? renderPolicyDocuments(policy) : renderQuoteDocuments(selectedQuote));
  }

  public openModal = (values, transactionId) => {
    this.setState({
      showModal: true,
      transactionId,
      values,
    });
  }

  public render() {
    const { auth, location, branding, quote, dispatch } = this.props;
    const { isRetrievingQuote } = this.state;

    let policy: IPolicy = null;
    let selectedQuote: IPolicyApplication = null;
    let isDisplayingPolicy = false;
    let displayCallMessage = false;
    let isDisplayingNewQuote = false;

    forEach(auth.customer.policies, (policySelected) => {
      if (location.pathname.match(policySelected.id)) {
        policy = policySelected;
        isDisplayingPolicy = true;
      }
    });

    forEach(auth.customer.policyApplications, (quoteSelected) => {
      if (location.pathname.match(quoteSelected.id)) {
        selectedQuote = quoteSelected;
        isDisplayingPolicy = false;
      }
    });

    // Added call-centre and pjh-b2b
    if (!['fps-b2c', 'fpr-b2c', 'npo-b2c', 'npor-b2c', 'cspa-b2c',
        'cspar-b2c', 'css-b2c', 'cssr-b2c', 'call-centre', 'pjh-b2b']
        .includes(get(selectedQuote, 'channel.key', 'fps-b2c'))) {
        isDisplayingNewQuote = true;
        displayCallMessage = true;
    }

    const title = isDisplayingPolicy ? `Policy ${policy.policyNumber}` : `Quote ${selectedQuote.quoteReference}`;
    let autoRenewalStatus = get(quote, 'autoRenewalStatus', '');
    if (!autoRenewalStatus || '' === autoRenewalStatus) {
      autoRenewalStatus = get(policy, 'renewalStatus', '');
    }

    return (
        <Row className="page-container">
            <Helmet>
                <title>{title}</title>
            </Helmet>
            <div className="customer-section-title">
                <h1>{title}</h1>
            </div>
            <div className="blue-section-group">
                <div className="blue-section">
                    <Row>
                        {this.renderCoverType(isDisplayingPolicy, policy, selectedQuote)}
                        {this.renderCoverDates(isDisplayingPolicy, policy, selectedQuote)}
                        {this.renderDestinations(isDisplayingPolicy, policy, selectedQuote)}
                        {this.renderOptions(isDisplayingPolicy, policy, selectedQuote)}
                        {this.renderStatus(isDisplayingPolicy, policy, selectedQuote)}
                        {this.renderPremium(isDisplayingPolicy, policy)}
                    </Row>
                </div>
                <div className="blue-section">
                    {this.renderDocuments(isDisplayingPolicy, policy, selectedQuote)}
                </div>
            </div>
            {displayCallMessage && (
                <div className="call-text-bar">
                    If you wish to continue with this quote please call us on {branding.channel.phone}.
                </div>
            )}
            {isDisplayingPolicy && (
                <NonAutoRenewalButton
                    policy={policy}
                    label="Submit"
                    dispatch={dispatch}
                    autoRenewalStatus={autoRenewalStatus}
                />
            )}
            {(!isDisplayingPolicy && !isDisplayingNewQuote) && (
                <div className="call-text-bar">
                    If you wish to edit your renewal, please call us on {branding.channel.phone}.
                </div>
            )}
            <div className="btn-bar">
                {!isDisplayingPolicy && (
                    <Button
                        bsStyle="primary"
                        className="pull-right btn-remove-border"
                        onClick={isDisplayingPolicy || isDisplayingNewQuote
                            ? this.handleGetNewQuote : this.handleEditQuote}
                        disabled={isRetrievingQuote}
                    >
                        {isRetrievingQuote && (<FaSpinner className="fa-spin"/>)}{' '}
                        {isDisplayingPolicy || isDisplayingNewQuote ? 'NEW QUOTE' : 'BUY NOW'}
                    </Button>
                )}
                <Link to="/account/summary" className="return-to-account-btn">
                    Return to account summary
                </Link>
            </div>
            {this.state.showModal && this.renderPaymentModal()}
        </Row>
    );
    }
}

let selector;

export default flow([
    reduxForm({
        form: 'quote',
        destroyOnUnmount: false,
        forceUnregisterOnUnmount: true,
    }),
    (component) => {
        selector = formValueSelector('quote');

        return component;
    },
    connect(
        (state: IStore) => {
            const tripType = selector(state, 'tripType');
            const groupType = selector(state, 'groupType');
            const startDate = selector(state, 'startDate');
            const endDate = selector(state, 'endDate');
            const destinationsValue = selector(state, 'destinations');
            const occupationValue = selector(state, 'occupations');
            const industryValue = selector(state, 'industries');
            const region = selector(state, 'region');
            const maxTripDuration = selector(state, 'maxTripDuration');

            return {
                tripType,
                region,
                maxTripDuration,
                groupType,
                startDate,
                endDate,
                destinationsValue,
                occupationValue,
                industryValue,
                auth: state.auth,
                quote: state.quote,
                branding: state.branding,
            };
        },
        (dispatch) => ({
            actions: {
                push: bindActionCreators(push, dispatch),
                auth: bindActionCreators({...authActions}, dispatch),
                // selectedQuote: bindActionCreators({ ...quoteActions }, dispatch),
                destroy: bindActionCreators(destroy, dispatch),
                quote: bindActionCreators({...quoteActions}, dispatch),
                branding: bindActionCreators({...brandingActions}, dispatch),
            },
        }),
    ),
])(PolicyDetail);



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/app/scenes/CustomerArea/scenes/PolicyDetail/index.tsx